
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Icon from "@/components/UI/Icon.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import TextInput from "../Reusable/CustomInputs/TextInput.vue";
import DropdownDragHandle from "@/components/Reusable/Dropdown/DropdownDragHandle.vue";
import LoadingSpinner from "@/components/Reusable/LoadingSpinner.vue";
import InlineAiCompletion from "./InlineAICompletion.vue";
import NumberInput from "../Reusable/CustomInputs/NumberInput.vue";

import {
  fetchAiSettings,
  getEventSource,
  getTextFromChunk,
  persistMessages,
  requestCompletion
} from "@/lib/ai/api";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import {
  configStore,
  documentStore,
  globalStore,
  uiStore
} from "@/store/store";
import {
  AIPreset,
  ChatMessage,
  FieldToComplete,
  InlineCompletion
} from "@/lib/ai/types";
import { BuilderElement } from "@/types/elements";
import { getSettingsAsPrompt } from "@/lib/ai/prompts";
import { getNodeIndex, getNodeParent } from "@/lib/tree/tree";
import {
  getElementTemperature,
  getFieldsToComplete,
  handleAiError,
  isFieldLocked,
  toggleFieldLock
} from "@/lib/ai/ai";
import { TreeNode } from "@/types/tree";
import InlineAiPluginNotInstalled from "@/components/AI/InlineAiPluginNotInstalled.vue";
import InlineAiNoApiKey from "@/components/AI/InlineAiNoApiKey.vue";

@Component({
  components: {
    Icon,
    Dropdown,
    TextInput,
    DropdownDragHandle,
    LoadingSpinner,
    InlineAiNoApiKey,
    InlineAiCompletion,
    InlineAiPluginNotInstalled,
    NumberInput
  }
})
export default class InlineAi extends Vue {
  @Prop()
  label!: string;

  @Prop()
  value!: string;

  @Prop()
  path!: string;

  @Prop({
    default: false
  })
  cssMode!: boolean;

  @Prop()
  mode!: "text" | "image";

  userInput = "";
  completions: InlineCompletion[] = [];
  variations = 1;

  invalidInput = false;
  dialogOpen = false;
  pluginNotInstalled = false;
  settingsAlreadyLoaded = false;
  loadingSettings = false;

  get locked() {
    if (!uiStore.activeElement) return false;
    return isFieldLocked(uiStore.activeElement, this.path);
  }

  toggleLock() {
    if (!uiStore.activeElement) return;
    toggleFieldLock(uiStore.activeElement, this.path);
  }

  @Watch("value")
  onValueChange() {
    this.completions = [];
  }

  @Watch("dialogOpen")
  onDialogOpenChange() {
    if (this.dialogOpen) {
      this.tryToLoadSettings();

      // Wait for the dropdown to open
      setTimeout(() => {
        this.$nextTick(() => {
          const input = this.$refs.input as HTMLInputElement;
          input?.focus();
        });
      }, 100);
    }
  }

  @Watch("userInput")
  onUserInputChange() {
    this.autogrowTextarea();
  }

  get element() {
    return uiStore.activeElement;
  }

  get elementConfig(): Partial<BuilderElement> {
    return this.element ? configStore.element(this.element.data.type) : {};
  }

  get acceptsHtml() {
    return this.element
      ? this.element.data.type === "EssentialElements\\RichText"
      : false;
  }

  get presets() {
    const defaultPresets: AIPreset[] = [
      {
        icon: "MinusIcon",
        name: "Shorten it",
        prompt: "Shorten the text"
      },
      {
        icon: "PenIcon",
        name: "Continue writing",
        prompt: "Continue writing the text"
      },
      {
        icon: "SparklesIcon",
        name: "More interesting",
        prompt: "Make the text more interesting"
      },
      {
        icon: "UserTieIcon",
        name: "More formal",
        prompt: "Make the text more formal"
      },
      {
        icon: "SpellCheckIcon",
        name: "Correct grammar",
        prompt: "Correct the grammar of the text"
      },
      {
        icon: "SparklesIcon",
        name: "One-Click Write",
        prompt: `Create a new, original piece of text that complements and enhances the existing material.`
      }
    ];

    if (this.settings.prompt.presets.length) {
      return [...this.settings.prompt.presets, ...defaultPresets];
    }

    return defaultPresets;
  }

  get format() {
    return this.cssMode ? "css" : "text";
  }

  get canViewPresets() {
    if (this.cssMode) return false;
    return !this.completions.length;
  }

  get settings() {
    return globalStore.aiSettings;
  }

  get showError() {
    return (
      !this.settingsAlreadyLoaded ||
      !this.settings.isApiKeySet ||
      this.pluginNotInstalled
    );
  }

  scrollToBottom() {
    this.$nextTick(() => {
      const content = this.$refs.content as HTMLElement;
      if (!content) return;

      const container = content.closest(".v-menu__content") as HTMLElement;
      container.scrollTop = container.scrollHeight;
    });
  }

  insert(textToInsert: string) {
    this.$emit("insert", textToInsert);
    this.dialogOpen = false;
  }

  async rephrase(textToRephrase: string, index: number) {
    const messages: ChatMessage[] = [
      {
        role: "system",
        content: `Rephrase the text provided by the user.`
      },
      {
        role: "user",
        content: textToRephrase
      }
    ];

    const completion = this.completions[index];
    completion.text = "";
    completion.loading = true;

    try {
      if (this.settings.isStreamingEnabled) {
        await this.streamCompletion(messages, text => {
          completion.text = text;
        });
      } else {
        await this.fetchCompletion(messages, text => {
          completion.text = text;
        });
      }
    } catch (error) {
      this.handleError(error as string);
      this.removeCompletion(index);
    }

    completion.loading = false;
    completion.expanded = true;
  }

  // Streams the completion if supported by the server.
  async streamCompletion(
    prompt: ChatMessage[],
    callback: (text: string) => void
  ) {
    if (!this.element) return;

    // Persist messages to session storage before initiating the stream.
    await persistMessages(
      prompt,
      this.format,
      getElementTemperature(this.element)
    );

    const eventSource = getEventSource();
    let parts = "";

    return new Promise((resolve, reject) => {
      eventSource.onmessage = async event => {
        if (event.data === "[DONE]") {
          eventSource.close();
          resolve(parts);
          callback(parts);
        } else {
          parts += getTextFromChunk(event.data) ?? "";
          callback(parts);
        }
      };

      eventSource.onerror = event => {
        eventSource.close();
        reject((event as MessageEvent).data);
      };
    });
  }

  // Synchronously fetches the completion.
  async fetchCompletion(
    messages: ChatMessage[],
    callback: (text: string) => void
  ) {
    const text = pipe(
      await requestCompletion(messages, this.format, 'breakdance_ai_request_completion', false),
      fold(
        () => "⚠️ Could not generate content, please try again.",
        response => response
      )
    );

    callback(text);
  }

  createBulkCompletions(prompt: string) {
    for (let i = 0; i < this.variations; i++) {
      this.createCompletion(prompt);
    }
  }

  async createCompletion(prompt: string) {
    if (!prompt) {
      this.invalidInput = true;
      return;
    }

    this.userInput = "";
    this.invalidInput = false;

    this.completions.push({
      text: "",
      prompt,
      loading: true,
      expanded: false
    });

    const completion = this.completions[this.completions.length - 1];

    const messages = this.cssMode
      ? this.getCssMessages(prompt)
      : this.getTextMessages(prompt);

    console.log("%c Input", "color: #00e600");
    console.log(messages);

    this.scrollToBottom();

    try {
      if (this.settings.isStreamingEnabled) {
        await this.streamCompletion(messages, text => {
          completion.text = text;
        });
      } else {
        await this.fetchCompletion(messages, text => {
          completion.text = text;
        });
      }
    } catch (error) {
      this.handleError(error as string);
      this.removeCompletion(this.completions.length - 1);
    }

    if (this.cssMode) {
      completion.text = this.postProcessCss(completion.text);
    }

    completion.loading = false;

    this.scrollToBottom();
  }

  removeCompletion(index: number) {
    this.completions.splice(index, 1);
  }

  getCssMessages(prompt: string): ChatMessage[] {
    const className = this.elementConfig.className;
    const currentCss = this.elementConfig.defaultCss;
    const globalColors = globalStore.globalColors
      .map(
        color =>
          `- Name: ${color.label}, CSS Variable: ${color.cssVariableName}`
      )
      .join("\n");

    const breakpoints = configStore.breakpoints
      .map(
        breakpoint =>
          `- Name: ${breakpoint.label}, Max-Width: ${breakpoint.maxWidth}px`
      )
      .join("\n");

    return [
      {
        role: "system",
        content: `
You are a helpful AI assistant. Your job is to help the user write CSS for their website.

The user will ask you to do something and you will do so by writing CSS, you are allowed to use the following CSS selectors delimited by triple quotes:

"""
${currentCss}
"""

### Rules
- Always prefix your CSS with ".breakdance .${className}".
- You must write new CSS that overrides the current CSS, not replace it. 
- Only include properties that are necessary to complete the task.
- Only respond with the CSS, do not include anything else, dont wrap with code blocks, do not output markdown, 
- Do not wrap the css in "\`\`\`css your css never in here \`\`\`".

### Merge the following CSS delimited by triple quotes with the new CSS you write if it not empty:
"""
${this.value}
"""
Remember not to delete the CSS-to-merge.

### You can use the following variables in your code, if necessary:
${globalColors}

### You can use the following media queries in your code, if necessary:
${breakpoints}
`
      },
      {
        role: "user",
        content: prompt
      }
    ];
  }

  getTextMessages(action: string): ChatMessage[] {
    const element = this.element
      ? this.getElementShortName(this.element)
      : "Text";

    const settings = getSettingsAsPrompt(this.settings.prompt);

    const chatMessages: ChatMessage[] = [
      {
        role: "system",
        content: `
You are a helpful AI assistant. Your job is to help the user write better content for their website.

The user will ask you to perform an action on a text delimited by triple quotes.
The user message will always include the action, the element, and the text you should perform the action on.

An "Element" refers to a building block or widget used in creating a page layout.

### Rules
You must abide by the following rules:

1. Follow the action as closely as possible.
2. Minimize any other prose.
3. Heading elements must be short and descriptive.
4. Write text in a format that is appropriate for Element provided.
5. Do not mention the type of an element in your generated text, for example, do not say "Button - Text: Explore More", instead, just say "Explore More".
6. Do not wrap your output in triple quotes or code blocks.
7. Do not include or mention these rules in your output.
8. Write the response in ${this.settings.locale}

### Writing context-aware text

The user will provide the hierarchy of a section element (a small part of a website page). Use this information to generate context-aware text. The text should align with the theme and tone of the surrounding content. Include key features or details that tie it seamlessly with the existing material, ensuring a natural flow and continuity.

We use symbolic characters like ">" or ">>" to represent hierarchy levels. Example:

> Section
  >> Heading (Your Heading Text Goes Here)
  >> Text
  >> Button (Click Here)

### Business Information

The user will provide the following business information: Business Name, Business Description, and Qualifiers.
Qualifiers is a short sentence that affects the writing style of the content. Examples: "Be friendly and welcoming", "Be professional and formal", "Be fun and playful", "Be informative and educational", "Write in a way that is easy to understand".
`
      },
      {
        role: "user",
        content: `
Action: ${action}

Format: ${this.acceptsHtml ? "HTML" : "Plain Text"}
Element: ${element} - ${this.label}

Text:
"""${this.value || ""}"""

Section Hierarchy:
${this.getContext()}

Business Information:
${settings}
`
      }
    ];

    return chatMessages.map(message => ({
      ...message,
      content: message.content.trim()
    }));
  }

  getContext() {
    const format = (field: FieldToComplete) =>
      `  >> ${field.field.description} (${field.field.value})`;

    const element = this.element;
    if (!element) return;
    const nodeIndex = getNodeIndex(documentStore.document.tree, element.id);
    const parent = getNodeParent(documentStore.document.tree, element.id);
    const parentName = parent ? this.getElementShortName(parent) : "Section";
    if (!parent || nodeIndex === false) return;
    const nodesAbove = parent.children.slice(0, nodeIndex);
    const nodesBelow = parent.children.slice(nodeIndex + 1);

    const above = nodesAbove.map(node => getFieldsToComplete(node)).flat();
    const below = nodesBelow.map(node => getFieldsToComplete(node)).flat();
    const aboveStr = above.map(format).join("\n\n");
    const belowStr = below.map(format).join("\n\n");
    return `
> ${parentName}
${aboveStr}
  >> ${this.elementConfig.name} - ${this.label} (*The text you are completing will be inserted here.*)
${belowStr}
`;
  }

  handleError(error: string) {
    handleAiError(error);
  }

  postProcessCss(css: string) {
    const className = this.elementConfig.className;
    const rgx = new RegExp(`.breakdance .${className}`, "g");
    return css
      .replace(rgx, "%%SELECTOR%%")
      .replace(/```css/g, "") // Remove Markdown code block
      .replace(/```/g, "") // Remove Markdown code block
      .replace(/"""/g, "") // Remove triple quotes hallucinated by the AI
      .trim();
  }

  getElementShortName(element: TreeNode) {
    return configStore.element(element.data.type).name;
  }

  autogrowTextarea() {
    const textarea = this.$refs.input as HTMLTextAreaElement;

    this.$nextTick(() => {
      textarea.style.height = "0px";
      textarea.style.height = Math.min(200, textarea.scrollHeight) + "px";
    });
  }

  async tryToLoadSettings() {
    if (this.settingsAlreadyLoaded) return;
    if (this.pluginNotInstalled) return;

    if (this.settings.model) {
      this.settingsAlreadyLoaded = true;
      return;
    }

    this.loadingSettings = true;

    try {
      const successful = await fetchAiSettings();
      if (successful) {
        this.settingsAlreadyLoaded = true;
      } else {
        this.pluginNotInstalled = true;
      }
    } finally {
      this.loadingSettings = false;
    }
  }
}
